import { useResolvedPath } from 'react-router-dom';
import {
    useAppointmentState,
    useAppointmentUpdateAndPersist,
    useEmptyAppointmentFactory,
    useTexting,
    TextOptInStatus,
    ContactPreferenceEnum,
    OptInStatus,
    useAppointmentDispatch,
    AppointmentDispatchTypes,
    useFormatAppointmentDateTime,
} from '../../../../context/Appointment/appointment';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { useState, useCallback } from 'react';
import { createAppointment, updatePickupAppointmentContact, IAppointment, ContactPreference, insertAuditTrail } from '../../../../API/GoMoto';
import { useIsHardwareConnected } from '../../../../context/hardware';
import { useDisplayLocalizedVehicle } from '../../../../helpers/displayVehicle';
import { AccountType, AppointmentLoanerOrRentalOption, Events, KioskTypeName } from '../../../../API/Enums';
import { DataBox, DataBoxItem } from '../../../../controls/DataBox/DataBox';
import { formatAsCurrency, formatAsPhoneNumber } from '../../../../helpers/Number';
import { textCapitalize } from '../../../../helpers/textCapitalize';
import EditIcon from '../../../../assets/icons/edit-box.svg?react';
import css from './Display.module.scss';
import { GMTitle } from '../../../../controls/Title';
import { GMButtonGroup } from '../../../../controls/ButtonGroup';
import { AdminButton } from '../../../../controls/AdminButton';
import { UpdateTotalDialog } from '../StoreKeyRoutes/UpdateTotal/UpdateTotal';
import { GMDialog, GMDialogInfo, GMDialogYesNo } from '../../../../controls/Dialog';
import { DisplayProps, DisplayFormType } from './Display.types';
import { useAdminState } from '../../../../context/Admin/admin';
import { useLocalization, useLocalizationExtension } from '../../../../context/Localization/localization';
import { Locales, LocalizationType } from '../../../../context/Localization/localization.types';
import { isDevelopment } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { IconButton } from '../../../../controls/IconButton/IconButton';
import { GMSizedIcon } from '../../../../controls/Icon';
import { CTAButton } from '../../../../controls/ButtonBase';
import { GMGrid } from '../../../../controls/Grid';
import { useSendPickupReceipt } from '../../../../API/GoMoto';
import { Snackbar } from '../../../../controls/Snackbar/Snackbar/Snackbar';
import { Snack } from '../../../../controls/Snackbar/Snackbar/Snackbar.types';
import { ScrollableBody } from '../../../Layout/ScrollableBody';
import { KeyManagementHeader } from '../../../../controls/KeyManagementHeader/KeyManagementHeader';
import { LegalDocumentLinks } from '../../../../controls/LegalDocumentLinks/LegalDocumentLinks';
import { KeyExchangeStage } from '../../../../context/Appointment/appointment.types';
import { useAdminKeyDropProcess, useAdminLockerOperationDispatch } from '../../../../context/AdminLockerOperation/adminLockerOperation';
import { AdminLockerOperationDispatch, AdminLockerOperationDispatchTypes } from '../../../../context/AdminLockerOperation/adminLockerOperation.types';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { Formik, FormikErrors } from 'formik';
import { GMFormCustomerDetailsPhoneField, GMFormEmailField } from '../../../../controls/TextField';
import { GMForm, GMFormControl } from '../../../../controls/Form';
import { nameof } from '../../../../helpers/nameof';
import { GMFormCheckbox } from '../../../../controls/Checkbox';
import { SimpleKeyboard } from '../../../../controls/SimpleKeyboard/SimpleKeyboard';
import { GMContactPreferenceButton } from '../../../../controls/ButtonBase';

/**
 * The `Display` component displays the appointment details after clicking on a card with admin buttons to perform
 * locker management operations.
 *
 * @param onStoreKey - {@link DisplayProps.onStoreKey onStoreKey} description.
 * @param onClose - {@link DisplayProps.onClose onClose} description.
 * @param onReturn - {@link DisplayProps.onReturn onReturn} description.
 */
export const Display: GoMotoComponent<DisplayProps> = ({
    onProceed,
    onStoreKey,
    onClose,
    onReturn,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { userID } = useAdminState();
    const appointment: IAppointment = useAppointmentState();
    const updateAppointment = useAppointmentUpdateAndPersist(true);
    const factory = useEmptyAppointmentFactory(false);
    const { updatePhoneOptInStatus } = useTexting();
    const { dealer: { customerTextingActive, name: dealerName }, key: kioskKey, name, kioskTypeName } = useKioskState();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const resend = useSendPickupReceipt();
    const { pathname } = useResolvedPath('.');
    const connected = useIsHardwareConnected();
    const adminLockerOperationDispatch: AdminLockerOperationDispatch = useAdminLockerOperationDispatch();
    const { AdminKeyDropProcess, showKeyDropProcessDialog, onMzeroRetrieveKeyClick } = useAdminKeyDropProcess({ onProceed: onProceed });
    const { emailInvalid, formatRequired, phoneInvalid } = useLocalizationExtension();
    const appointmentDispatch = useAppointmentDispatch();
    const { formatDateTimeWithCustomTimeFormat } = useFormatAppointmentDateTime();

    const [showUpdateTotal, setShowUpdateTotal] = useState<boolean>(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
    const [showResendNotificationDialog, setShowResendNotificationDialog] = useState<boolean>(false);
    const [snack, setSnack] = useState<Snack | undefined>();
    const [optInStatus, setOptInStatus] = useState<OptInStatus | undefined>(undefined);
    const [showOptInCheckbox, setShowOptInCheckbox] = useState<boolean>(false);
    const [showNotDeliverableDialog, setShowNotDeliverableDialog] = useState<boolean>(false);

    const canUpdateTotal: boolean = appointment.paymentStatus === 'not paid';
    const towIn: boolean = !!appointment.towInCompanyName;
    const canStoreKey: boolean | undefined = !towIn && connected() && !appointment.keySlot && appointment.status !== 'completed';
    const isExpressOrThe365: boolean = kioskTypeName === KioskTypeName.TheExpress || kioskTypeName === KioskTypeName.The365;
    const isThe247: boolean = kioskTypeName === KioskTypeName.The247;
    const canRetrieveExchangedKey: boolean = isExpressOrThe365 && appointment.keyExchangeStage === KeyExchangeStage.AdminKeyRetrieve;
    const canRetrieveKey: boolean | undefined = connected() && !!appointment.keySlot;
    const canDelete: boolean = !appointment.keySlot && appointment.status !== 'completed';
    const contactPreference: ContactPreference | undefined = appointment.storedByAdvisor === true
        && appointment.keyStatus === 'slot_assigned' ? appointment.pickupContactPreference : appointment.contactPreference;

    const deleteAppointment: VoidFunction = async () => {
        setShowConfirmDelete(false);
        await updateAppointment({ ...appointment,
            status: 'completed' }, false, false, false, true, undefined, false, false, false, false, false, true);
        onClose();
    };

    const initialValues: DisplayFormType = {
        phone: canRetrieveKey && appointment.pickupPhone ? appointment.pickupPhone : appointment.phone,
        email: canRetrieveKey && appointment.pickupEmail ? appointment.pickupEmail : appointment.email,
        textOptInStatus: optInStatus !== undefined ? optInStatus.status : TextOptInStatus.NotDecided,
    };

    const customerDetails = useCallback((): DataBoxItem[] => {
        const customerDetails: DataBoxItem[] = [
            {
                title: Localization.Phone,
                value: (formatAsPhoneNumber(canRetrieveKey && appointment.pickupPhone ? appointment.pickupPhone : appointment.phone ?? '')),
            },
            {
                title: Localization.Email,
                value: canRetrieveKey && appointment.pickupEmail ? appointment.pickupEmail : appointment.email,
            },
        ];

        /*
         * TODO: given that we have used something like this in several places to map a contact pref to the localization,
         * we should make a map of the contact preference type to the Localization related for each contact pref.
         */
        if (contactPreference === 'phone') {
            customerDetails.push({
                title: Localization.ContactPreference,
                value: Localization.Phone,
                case: 'capitalize',
            });
        }
        else if (contactPreference === 'email') {
            customerDetails.push({
                title: Localization.ContactPreference,
                value: Localization.Email,
                case: 'capitalize',
            });
        }
        else if (contactPreference === 'text') {
            customerDetails.push({
                title: Localization.ContactPreference,
                value: Localization.Text,
                case: 'capitalize',
            });
        }
        else if (contactPreference === 'both') {
            customerDetails.push({
                title: Localization.ContactPreference,
                value: Localization.Both,
                case: 'capitalize',
            });
        }

        if (!towIn) {
            if (appointment.locale === Locales.es) {
                customerDetails.push({
                    title: Localization.Language,
                    value: Localization.Spanish,
                    case: 'capitalize',
                });
            }
            else if (appointment.locale === Locales.en) {
                customerDetails.push({
                    title: Localization.Language,
                    value: Localization.English,
                    case: 'capitalize',
                });
            }
            else if (appointment.locale === Locales.fr) {
                customerDetails.push({
                    title: Localization.Language,
                    value: Localization.French,
                    case: 'capitalize',
                });
            }
        }

        if (appointment.accountType === AccountType.business || appointment.firstName === null) {
            const databoxBusinessName = appointment.businessName || appointment.lastName || '';
            customerDetails.unshift({
                title: towIn ? Localization.Name : Localization.Business,
                value: textCapitalize(databoxBusinessName, true),
            });
        }
        else {
            customerDetails.unshift({
                title: Localization.LastName,
                value: textCapitalize(appointment.lastName),
            });
            customerDetails.unshift({
                title: Localization.FirstName,
                value: textCapitalize(appointment.firstName),
            });
        }

        return customerDetails;
    }, [
        Localization.Both,
        Localization.Business,
        Localization.ContactPreference,
        Localization.Email,
        Localization.English,
        Localization.FirstName,
        Localization.French,
        Localization.Language,
        Localization.LastName,
        Localization.Name,
        Localization.Phone,
        Localization.Spanish,
        Localization.Text,
        appointment.accountType,
        appointment.businessName,
        appointment.email,
        appointment.pickupEmail,
        appointment.firstName,
        appointment.lastName,
        appointment.locale,
        appointment.phone,
        appointment.pickupPhone,
        canRetrieveKey,
        contactPreference,
        towIn,
    ]);

    const vehicleDetails = (): DataBoxItem[] => {
        const vehicleDetails: DataBoxItem[] = [];

        if (canRetrieveExchangedKey) {
            vehicleDetails.push(
                {
                    title: Localization.VehicleType,
                    value: appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? Localization.Loaner : Localization.Rental,
                },
                {
                    title: Localization.KeyReturned,
                    value: appointment.loanerOrRentalReturnTimestamp ? formatDateTimeWithCustomTimeFormat({
                        appointmentDateTime: appointment.loanerOrRentalReturnTimestamp,
                        timeFormats: {
                            [Locales.en]: 'MM/DD/YYYY, hh:mm A',
                            [Locales.es]: 'MM/DD/YYYY, hh:mm A',
                            [Locales.fr]: 'DD/MM/YYYY, H [h] mm',
                        },
                    }) : Localization.DateError,
                },
            );
        }
        else {
            vehicleDetails.push({
                title: Localization.Vehicle,
                value: displayLocalizedVehicle(appointment)
                    .toString(),
            });

            if (!towIn) {
                vehicleDetails.push({
                    title: Localization.Mileage,
                    value: appointment.mileage?.toString(),
                });
            }
        }

        return vehicleDetails;
    };

    const transactionDetails = (): DataBoxItem[] => {
        let appointmentStatusDisplay = '';
        if (towIn) {
            appointmentStatusDisplay = Localization.VehicleTowIn;
        }
        else {
            switch (appointment.status) {
                case 'not scheduled':
                    appointmentStatusDisplay = Localization.NotScheduled;
                    break;
                case 'scheduled':
                    appointmentStatusDisplay = Localization.Scheduled;
                    break;
                case 'completed':
                    appointmentStatusDisplay = Localization.Completed;
                    break;
                case 'loaner returned':
                    appointmentStatusDisplay = Localization.LoanerKeyReturned;
                    break;
                case 'rental returned':
                    appointmentStatusDisplay = Localization.RentalKeyReturned;
                    break;
            }
        }
        let keyStatusDisplay = '';
        switch (appointment.keyStatus) {
            case 'none':
                keyStatusDisplay = Localization.None;
                break;
            case 'slot_assigned':
                keyStatusDisplay = Localization.SlotAssigned;
                break;
            case 'picked up':
                keyStatusDisplay = Localization.PickedUp;
                break;
            case 'reopen requested pickup':
                keyStatusDisplay = Localization.ReopenRequestedPickup;
                break;
            case 'reopen requested dropoff':
                keyStatusDisplay = Localization.ReopenRequestedDropoff;
                break;
            case 'loaner key stored':
            case 'rental key stored':
            case 'reopen requested exchange':
                keyStatusDisplay = Localization.KeyLoadedInKiosk;
                break;
        }

        const dataItems: DataBoxItem[] =
            towIn ?
                [
                    {
                        title: Localization.AppointmentStatus,
                        value: appointmentStatusDisplay,
                    },
                    {
                        title: Localization.KeyStatus,
                        value: keyStatusDisplay,
                    },
                    {
                        title: Localization.KioskLocation,
                        value: name,
                    },
                    {
                        title: Localization.KeySlot,
                        value: '' + (appointment.keySlotNumber || ''),
                    },
                ]
                :
                [
                    {
                        title: Localization.RONumber,
                        value: appointment.roNumber,
                    },
                    {
                        title: Localization.AppointmentStatus,
                        value: appointmentStatusDisplay,
                    },
                    {
                        title: Localization.KeyStatus,
                        value: keyStatusDisplay,
                    },
                    {
                        title: Localization.KioskLocation,
                        value: name,
                    },
                    {
                        title: Localization.KeySlot,
                        value: '' + (appointment.keySlotNumber || ''),
                    },
                    {
                        title: Localization.QRStatus,
                        value: appointment.qr_status,
                    },
                ];

        return dataItems;
    };

    const devDetails = (): DataBoxItem[] => {
        const devDetails: DataBoxItem[] = [{
            title: Localization.AppointmentKey,
            value: appointment.key,
        }];

        return devDetails;
    };

    const paymentStatus = (paymentStatus: IAppointment['paymentStatus']): string => {
        switch (paymentStatus) {
            case 'not paid':
                return Localization.PaymentRequired;
            case 'paid':
                return Localization.PaymentReceived;
            default:
                return paymentStatus;
        }
    };

    const paymentDetails = (): DataBoxItem[] => {
        const dataItems: DataBoxItem[] = [
            {
                title: Localization.PaymentStatus,
                value: paymentStatus(appointment.paymentStatus),
                case: 'capitalize',
            },
            {
                title: Localization.PaymentAmount,
                value: formatAsCurrency(appointment.serviceTotal),
                valueIcon: canUpdateTotal ? <EditIcon onClick={() => setShowUpdateTotal(true)} className={css.editIcon} /> : undefined,
            },
        ];

        return dataItems;
    };

    const onRetrieveKeyClick: VoidFunction = () => {
        adminLockerOperationDispatch({
            type: AdminLockerOperationDispatchTypes.SetReturnPath,
            payload: pathname,
        });

        if (canRetrieveExchangedKey) {
            showKeyDropProcessDialog('return-key');
        }
        else {
            if (appointment.paymentStatus === 'not paid') {
                showKeyDropProcessDialog('payment-required');
            }
            else {
                if (!isThe247) {
                    showKeyDropProcessDialog('return-key');
                }
                else {
                    onMzeroRetrieveKeyClick && onMzeroRetrieveKeyClick(undefined);
                }
            }
        }
    };

    return (
        <ScrollableBody
            heading={<KeyManagementHeader />}
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        <CTAButton
                            onClick={() => onReturn()}
                            styleType={'secondary'}
                            label={Localization.Return}
                            kioskOptions={{
                                inlineOptions: {
                                    leftIcon: true,
                                    sizedIcon: (
                                        <GMSizedIcon
                                            iconSize={'custom'}
                                            iconType={'Arrow-Back'}
                                        />
                                    ),
                                },
                            }}
                            id={Localization.Return}
                            ariaLabel={Localization.Return}
                        />,
                    ]}
                />
            }
        >
            <div>
                <Snackbar snack={snack} />
                <div className={css.details}>
                    <GMTitle headerLevel={4}>{Localization.CustomerDetails}</GMTitle>
                    <DataBox dataItems={customerDetails()} />
                    <GMTitle headerLevel={4}>{canRetrieveExchangedKey ? Localization.VehicleReturnDetails : Localization.VehicleDetails}</GMTitle>
                    <DataBox dataItems={vehicleDetails()} />
                    <GMTitle headerLevel={4}>{Localization.TransactionDetails}</GMTitle>
                    <DataBox dataItems={transactionDetails()} />
                    {appointment.paymentStatus !== 'no payment due' ? (
                        <>
                            <GMTitle headerLevel={4}>{Localization.PaymentDetails}</GMTitle>
                            <DataBox dataItems={paymentDetails()} />
                        </>
                    ) : null}
                    {isDevelopment() && (
                        <>
                            <GMTitle headerLevel={4}>{'Dev Only'}</GMTitle>
                            <DataBox dataItems={devDetails()} />
                        </>
                    )}
                </div>
                <GMButtonGroup
                    alignment={'left'}
                    buttons={[
                        (canDelete ? (
                            <AdminButton
                                onClick={() => setShowConfirmDelete(true)}
                                label={Localization.DeleteAppointment}
                                id={Localization.DeleteAppointment}
                                ariaLabel={Localization.DeleteAppointment}
                            />
                        ) : null),
                        (canUpdateTotal ? (
                            <AdminButton
                                onClick={() => updateAppointment({ ...appointment,
                                    serviceTotal: 0,
                                    paymentStatus: 'marked paid' }, false, false, false, true, undefined, false, false, false, false, false, true)}
                                label={Localization.MarkAsPaid}
                                id={Localization.MarkAsPaid}
                                ariaLabel={Localization.MarkAsPaid}
                            />
                        ) : null),
                        (canStoreKey ? (
                            <AdminButton
                                onClick={() => {
                                    (async () => {
                                        const emptyAppt = factory();
                                        appointment.key ||
                                            (await updateAppointment({
                                                ...emptyAppt,
                                                ...appointment,
                                                key: await createAppointment(kioskKey, { ...emptyAppt,
                                                    ...appointment }),
                                            }, false, false, false, true, undefined, false, false, false, false, false, true));
                                        onStoreKey();
                                    })();
                                }}
                                label={Localization.StoreKeyForReturn}
                                id={Localization.StoreKeyForReturn}
                                ariaLabel={Localization.StoreKeyForReturn}
                            />
                        ) : null),
                        (canRetrieveKey && !isThe247 ? (
                            <AdminButton
                                onClick={() => {
                                    adminLockerOperationDispatch({
                                        type: AdminLockerOperationDispatchTypes.SetReturnPath,
                                        payload: pathname,
                                    });
                                    showKeyDropProcessDialog('open-locker');
                                }}
                                type={'button'}
                                theme={'secondary'}
                                label={Localization.OpenLocker}
                                id={Localization.OpenLocker}
                                ariaLabel={Localization.OpenLocker}
                            />
                        ) : null),
                        (canRetrieveKey ? (
                            <AdminButton
                                onClick={onRetrieveKeyClick}
                                type={'button'}
                                theme={kioskTypeName !== KioskTypeName.The247 ? 'secondary' : 'primary'}
                                label={Localization.RetrieveKey}
                                id={Localization.RetrieveKey}
                                ariaLabel={Localization.RetrieveKey}
                            />
                        ) : null),
                        (canRetrieveKey && appointment.storedByAdvisor && !canRetrieveExchangedKey ? (
                            <AdminButton
                                onClick={() => setShowResendNotificationDialog(true)}
                                label={Localization.ResendNotification}
                                id={Localization.ResendNotification}
                                ariaLabel={Localization.ResendNotification}
                            />
                        ) : null),
                    ]}
                />
                {canDelete ? (
                    <GMDialogYesNo
                        ariaLabel={Localization.Warning}
                        title={Localization.Warning}
                        open={showConfirmDelete}
                        onNo={() => setShowConfirmDelete(false)}
                        onYes={deleteAppointment}
                    >
                        {Localization.AreYouSureYouWantToDeleteThisAppointment}
                    </GMDialogYesNo>
                ) : null}
                {canUpdateTotal ? (
                    <UpdateTotalDialog
                        total={appointment.serviceTotal || 0}
                        open={showUpdateTotal}
                        onClose={() => setShowUpdateTotal(false)}
                        onConfirm={async (newTotal) => {
                            await updateAppointment({ ...appointment,
                                serviceTotal: newTotal }, false, false, false, true, undefined, false, false, false, false, false, true);
                            setShowUpdateTotal(false);
                        }}
                    />
                ) : null}
                {canRetrieveKey ? (
                    <AdminKeyDropProcess />
                ) : null}
                <GMDialog
                    ariaLabel={Localization.ResendNotification}
                    open={showResendNotificationDialog}
                    onClose={() => setShowResendNotificationDialog(false)}
                    paperDialogClasses={css.resendDialog}
                    rootDialogContentClasses={css.resendDialogContent}
                >
                    <div className={css.closeIconContainer}>
                        <div>
                            <IconButton
                                className={css.closeIcon}
                                key={'em-sr-close'}
                                sizedIcon={
                                    <GMSizedIcon
                                        iconType={'Close'}
                                        iconSize={'small'}
                                        ariaLabel={Localization.CloseIcon}
                                        title={Localization.ClickToCloseWindow}
                                    />
                                }
                                onClick={() => {
                                    setShowResendNotificationDialog(false);
                                }}
                                ariaLabel={Localization.CloseIcon}
                                id={Localization.CloseIcon}
                            />
                        </div>
                    </div>
                    <Formik
                        initialValues={initialValues}
                        validate={async (values) => {
                            const errors: FormikErrors<DisplayFormType> = {};

                            const newPhone = values.phone?.trim();
                            const newEmail = values.email?.trim();
                            const phoneRequired: boolean = values.contactPreference === 'text' || values.contactPreference === 'both';
                            const emailRequired: boolean = values.contactPreference === 'email' || values.contactPreference === 'both';

                            if (phoneRequired) {
                                if (newPhone.length === 0) {
                                    errors.phone = formatRequired(Localization.Phone);
                                }
                                else if (newPhone.length === 10) {
                                    if (optInStatus?.isDeliverable === false) {
                                        errors.phone = Localization.ThisPhoneNumberCannotReceiveTextNotifications;
                                    }
                                    else if (optInStatus?.status === TextOptInStatus.OptedOut) {
                                        errors.phone = Localization.PhoneNumberHasOptedOut;
                                    }
                                    else if (showOptInCheckbox && values.textOptInStatus !== TextOptInStatus.OptedIn) {
                                        errors.textOptInStatus = 'textOptInStatus is invalid';
                                    }
                                }
                            }

                            if (emailRequired && (!newEmail || newEmail.length === 0)) {
                                errors.email = formatRequired(Localization.Email);
                            }

                            if (newEmail?.length > 0 && !(/(.{1,})(@{1})(.{1,}\.{1}.{1,})/).test(newEmail)) {
                                errors.email = emailInvalid;
                            }

                            if (newPhone.length > 0 && newPhone.length !== 10) {
                                errors.phone = phoneInvalid;
                            }

                            return errors;
                        }}
                        onSubmit={async (values, { resetForm }) => {
                            const newPhone: string = values.phone?.trim();
                            const newEmail: string = values.email?.trim();
                            const newContactPreference: ContactPreference | undefined = values.contactPreference;
                            const phoneChanged: boolean = newPhone !== initialValues.phone;
                            const emailChanged: boolean = newEmail !== initialValues.email;
                            const contactPreferenceChanged: boolean = newContactPreference !== initialValues.contactPreference;
                            const wasTextOptedIn: boolean = (
                                initialValues.textOptInStatus === TextOptInStatus.NotDecided
                                || initialValues.textOptInStatus === TextOptInStatus.OptedOut
                            )
                                && values.textOptInStatus === TextOptInStatus.OptedIn;

                            if (wasTextOptedIn) {
                                await updatePhoneOptInStatus(newPhone, true)
                                    .then(() => {
                                        insertAuditTrail(appointment.key, Events.Text_Opt_In);
                                    });
                            }

                            if (phoneChanged || emailChanged || contactPreferenceChanged) {
                                await updatePickupAppointmentContact({
                                    appointmentKey: appointment.key,
                                    pickupEmail: emailChanged ? newEmail : undefined,
                                    pickupPhone: phoneChanged ? newPhone : undefined,
                                    pickupContactPreference: contactPreferenceChanged ? newContactPreference : undefined,
                                });
                            }

                            appointmentDispatch({
                                type: AppointmentDispatchTypes.Update,
                                payload: {
                                    ...appointment,
                                    pickupPhone: newPhone,
                                    pickupEmail: newEmail,
                                },
                            });

                            await resend({
                                appointmentKey: appointment.key,
                                sendText: values.contactPreference === ContactPreferenceEnum.Text || values.contactPreference === ContactPreferenceEnum.Both,
                                sendEmail: values.contactPreference === ContactPreferenceEnum.Email || values.contactPreference === ContactPreferenceEnum.Both,
                                userId: userID,
                                resend: true,
                                locale: appointment.locale,
                                shouldUsePickupPhone: !!appointment.pickupPhone || phoneChanged,
                                shouldUsePickupEmail: !!appointment.pickupEmail || emailChanged,
                            })
                                .finally(() => {
                                    setSnack({
                                        key: `snack-${Math.random()}`,
                                        onClose: () => setSnack(undefined),
                                        label: Localization.PickupNotificationSent,
                                    });
                                    setShowResendNotificationDialog(false);

                                    resetForm({
                                        values: {
                                            phone: newPhone,
                                            email: newEmail,
                                            textOptInStatus: values.textOptInStatus,
                                            contactPreference: undefined,
                                        },
                                    });
                                });
                        }}
                    >
                        {({ submitForm, resetForm, values, errors, setFieldValue, isSubmitting }) => {
                            return (
                                <>
                                    <GMForm hideSpacer hideKeyboard>
                                        <GMGrid container rowGap={2}>
                                            <GMGrid
                                                xs={12}
                                                textAlign={'center'}
                                                className={css.title}
                                            >
                                                {Localization.ResendPickupNotification}
                                            </GMGrid>
                                            <GMGrid xs={8} xsOffset={2}>
                                                <GMFormEmailField
                                                    name={nameof<typeof initialValues>('email')}
                                                    id={nameof<typeof initialValues>('email')}
                                                    title={Localization.EmailAddress}
                                                    label={Localization.EmailAddress}
                                                    ariaLabel={Localization.EmailAddress}
                                                />
                                            </GMGrid>
                                            {customerTextingActive &&
                                                <>
                                                    <GMGrid xs={8} xsOffset={2}>
                                                        <GMFormCustomerDetailsPhoneField
                                                            name={nameof<typeof initialValues>('phone')}
                                                            id={nameof<typeof initialValues>('phone')}
                                                            title={Localization.Phone}
                                                            label={Localization.Phone}
                                                            ariaLabel={Localization.Phone}
                                                            optInStatusFieldName={nameof<DisplayFormType>('textOptInStatus')}
                                                            optInStatus={optInStatus}
                                                            setOptInStatus={(optInStatus: OptInStatus | undefined) => {
                                                                setOptInStatus(optInStatus);
                                                                setShowOptInCheckbox(optInStatus?.isDeliverable !== false && optInStatus?.status === TextOptInStatus.NotDecided);
                                                            }}
                                                            textingActive={customerTextingActive}
                                                            noValidation
                                                        />
                                                    </GMGrid>
                                                    {showOptInCheckbox ? (
                                                        <GMGrid
                                                            id={'customer-details-opt-in-container'}
                                                            container
                                                            className={css.textOptIn}
                                                        >
                                                            <GMGrid xs={10} xsOffset={1}>
                                                                <GMFormControl className={css.textOptInError} error={!!errors.textOptInStatus}>
                                                                    <GMFormCheckbox
                                                                        label={
                                                                            Localization.formatString(
                                                                                Localization.ByCheckingThisBoxCustomerAgreesToReceiveTextMessages,
                                                                                textCapitalize(appointment.firstName),
                                                                                dealerName,
                                                                            ) as string
                                                                        }
                                                                        labelVariant={'none'}
                                                                        checked={values.textOptInStatus === TextOptInStatus.OptedIn}
                                                                        errorClassName={css.checkboxError}
                                                                        onChange={() => {
                                                                            if (values.textOptInStatus === TextOptInStatus.OptedIn) {
                                                                                setFieldValue(nameof<DisplayFormType>('textOptInStatus'), TextOptInStatus.NotDecided);
                                                                            }
                                                                            else {
                                                                                setFieldValue(nameof<DisplayFormType>('textOptInStatus'), TextOptInStatus.OptedIn);
                                                                            }
                                                                        }}
                                                                    />
                                                                </GMFormControl>
                                                                <LegalDocumentLinks />
                                                            </GMGrid>
                                                        </GMGrid>
                                                    ) : null}
                                                </>
                                            }
                                            <GMGrid
                                                xs={12}
                                                container
                                                columnSpacing={3}
                                                padding={2}
                                                margin={0}
                                            >
                                                {customerTextingActive ?
                                                    <>
                                                        <GMGrid xs={4}>
                                                            <GMContactPreferenceButton
                                                                name={'email-button'}
                                                                contactPreference={ContactPreferenceEnum.Email}
                                                                onClick={async () => {
                                                                    await setFieldValue(nameof<DisplayFormType>('contactPreference'), 'email');
                                                                    await Promise.resolve();
                                                                    submitForm();
                                                                }}
                                                                disabled={isSubmitting}
                                                            />
                                                        </GMGrid>
                                                        <GMGrid xs={4}>
                                                            <GMContactPreferenceButton
                                                                name={'text-button'}
                                                                contactPreference={ContactPreferenceEnum.Text}
                                                                onClick={async () => {
                                                                    await setFieldValue(nameof<DisplayFormType>('contactPreference'), 'text');
                                                                    await Promise.resolve();
                                                                    submitForm();
                                                                }}
                                                                disabled={isSubmitting}
                                                            />
                                                        </GMGrid>
                                                        <GMGrid xs={4}>
                                                            <GMContactPreferenceButton
                                                                name={'both-button'}
                                                                contactPreference={ContactPreferenceEnum.Both}
                                                                onClick={async () => {
                                                                    await setFieldValue(nameof<DisplayFormType>('contactPreference'), 'both');
                                                                    await Promise.resolve();
                                                                    submitForm();
                                                                }}
                                                                disabled={isSubmitting}
                                                            />
                                                        </GMGrid>
                                                    </> :
                                                    <GMGrid xs={12}>
                                                        <CTAButton
                                                            styleType={'primary'}
                                                            label={Localization.SendNow}
                                                            id={Localization.SendNow}
                                                            ariaLabel={Localization.SendNow}
                                                            onClick={async () => {
                                                                await setFieldValue(nameof<DisplayFormType>('contactPreference'), 'email');
                                                                await Promise.resolve();
                                                                submitForm();
                                                            }}
                                                            disabled={isSubmitting}
                                                        />
                                                    </GMGrid>
                                                }
                                            </GMGrid>
                                        </GMGrid>
                                        <GMDialogInfo
                                            ariaLabel={Localization.PhoneNumberForTextMessages}
                                            ariaDescribedBy={'customer-details-' + Localization.PhoneNumberForTextMessages}
                                            className={css.dialogTitle}
                                            paperDialogClasses={css.dialog}
                                            title={Localization.PhoneNumberForTextMessages}
                                            onClose={async () => {
                                                await resetForm({
                                                    values: {
                                                        ...values,
                                                        contactPreference: undefined,
                                                    },
                                                });

                                                setShowNotDeliverableDialog(false);
                                            }}
                                            closeButtonText={Localization.OK}
                                            open={showNotDeliverableDialog}
                                        >
                                            <GMGrid container id={'customer-details-' + Localization.PhoneNumberForTextMessages + '-container'}>
                                                <GMGrid
                                                    id={'customer-details-' + Localization.OhNoItLooksLikeYourPhoneNumberIsNotDeliverable + '-grid'}
                                                    className={css.dialogBody}
                                                    xs={12}
                                                >
                                                    {Localization.OhNoItLooksLikeYourPhoneNumberIsNotDeliverable}
                                                </GMGrid>
                                                <GMGrid
                                                    id={'customer-details-' + Localization.PleaseEnterADifferentPhoneNumber + '-grid'}
                                                    className={css.dialogBody}
                                                    xs={12}
                                                >
                                                    {Localization.PleaseEnterADifferentPhoneNumber}
                                                </GMGrid>
                                            </GMGrid>
                                        </GMDialogInfo>
                                    </GMForm>
                                </>
                            );
                        }}
                    </Formik>
                </GMDialog>
            </div>
            {showResendNotificationDialog && <SimpleKeyboard fromModal />}
        </ScrollableBody>
    );
};